import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCheck, faListCheck, faSliders } from '@fortawesome/free-solid-svg-icons'
import { opisOdpowiedzi } from './KreatorSzkielet'
import '../rynek/Zlecenie.css'
import './KreatorKrokowy.css'

/**
 * Kreator krokowy: jedno pytanie na ekranie (droga „Kilka pytań” w formularzu zlecenia, dopisek właściciela
 * 2026-10-26: porządnie, nowocześnie i prosto). Te same definicje pytań co `KreatorSzkielet` (`warunek`, `typ`:
 * wybór, `wielokrotny`, `liczba` z `jednostki`, `lista`, `komponent`), inny układ:
 *
 * 1. najpierw tylko pytania z `wymagane` („Krok 1 z 2” i pasek postępu);
 * 2. rozdroże: „Doprecyzuj” (pytania dodatkowe, każde z dyskretnym „Pomiń”) albo od razu podsumowanie;
 * 3. podsumowanie z „Zmień” przy każdej pozycji i przyciskiem wyniku (`tekstPrzycisku`, napis albo funkcja odpowiedzi).
 *
 * „Wstecz” i „Dalej” stoją zawsze w tej samej stopce (przyklejonej do dołu ekranu na telefonie). Klawiatura: Enter to
 * „Dalej”, cyfry 1 do 9 wybierają odpowiedź, Tab przechodzi po kaflach. Odpowiedzi ukrytych pytań zostają w stanie
 * (pytanie może wrócić), a do `onDobierz` idą tylko odpowiedzi widocznych pytań.
 */

const ROZDROZE = '__rozdroze'
const PODSUMOWANIE = '__podsumowanie'

const wypelnione = (p, v) => {
  if (v === undefined || v === '') return false
  if (p.jednostki) return v === null || v?.ile != null
  return true
}

/** Robocza wartość pytania na ekranie: to, co już odpowiedziano, albo pusty stan dla typu. */
const robocza = (p, v) => {
  if (p.typ === 'wielokrotny') return Array.isArray(v) ? v : []
  if (p.typ === 'komponent') return v ?? p.pusta ?? null
  if (p.typ === 'liczba') {
    const jednostka = p.jednostki ? (v?.jednostka ?? p.jednostki[0].v) : null
    const ile = p.jednostki ? v?.ile : v
    return { tekst: ile == null ? '' : String(ile).replace('.', ','), jednostka }
  }
  return v
}

/** Czy robocza wartość wystarcza na „Dalej”. */
const gotowa = (p, r) => {
  if (p.typ === 'wielokrotny') return r.length > 0
  if (p.typ === 'komponent') return p.gotowe ? p.gotowe(r) : true
  if (p.typ === 'liczba') {
    const n = Number(String(r.tekst).replace(',', '.'))
    return String(r.tekst).trim() !== '' && Number.isFinite(n) && n > 0
  }
  return r !== undefined && r !== null && r !== ''
}

/** Robocza wartość jako odpowiedź. */
const odpowiedzZRoboczej = (p, r) => {
  if (p.typ !== 'liczba') return r
  const n = Number(String(r.tekst).replace(',', '.'))
  return p.jednostki ? { ile: n, jednostka: r.jednostka } : n
}

// Karta odpowiedzi (J6, 2026-10-26): pionowa, ikona u góry, tytuł i krótki opis pod spodem, numer albo znak w rogu.
const Kafel = ({ o, zaznaczony, wielokrotny, numer, onClick }) => (
  <button
    type="button" role={wielokrotny ? 'checkbox' : 'radio'} aria-checked={zaznaczony}
    className={`kk-karta${zaznaczony ? ' kk-karta-tak' : ''}`} onClick={onClick}
  >
    <span className="kk-karta-gora">
      {o.ikona
        ? <span className="kk-kafel-ikona" aria-hidden="true"><FontAwesomeIcon icon={o.ikona} /></span>
        : <span />}
      <span className="kk-kafel-znak" aria-hidden="true">
        {zaznaczony ? <FontAwesomeIcon icon={faCheck} /> : numer <= 9 ? numer : ''}
      </span>
    </span>
    <span className="kk-kafel-tytul">{o.l}</span>
    {o.o && <span className="kk-kafel-opis">{o.o}</span>}
  </button>
)

// Kolumny siatki kart na szerszym ekranie: 3, a przy 2 i 4 odpowiedziach 2 (bez sierot w ostatnim rzędzie).
const kolumny = (n) => (n === 2 || n === 4 ? 2 : 3)

/**
 * `onEkran(nazwa)`: każdy pokazany ekran (pomiar ścieżki /rynek, 2026-10-28).
 * `onOdNowa`: „Zacznij od nowa” na podsumowaniu (po potwierdzeniu) czyści odpowiedzi kreatora; strona usuwa przy tym
 * zapisane odpowiedzi (J7). Pytanie może mieć `etykietaOd(o)` (inna etykieta w podsumowaniu, np. przy rozbudowie)
 * i `wPodsumowaniu(o)`: wiersz `{ wartosc, zmien }`, gdy pytanie jest ukryte, bo przesądziła je inna odpowiedź.
 */
const KreatorKrokowy = ({
  pytania, wymagane = [], domyslne = {}, poczatkowe = null, onZmiana = null, onDobierz, tekstPrzycisku, onOdNowa = null,
  onEkran = null,
}) => {
  const { t } = useTranslation('asystent')
  const [odpowiedzi, setOdpowiedzi] = useState(poczatkowe || domyslne)
  useEffect(() => { onZmiana?.(odpowiedzi) }, [odpowiedzi]) // eslint-disable-line react-hooks/exhaustive-deps

  const widoczne = useMemo(() => pytania.filter((p) => !p.warunek || p.warunek(odpowiedzi)), [pytania, odpowiedzi])
  const wymaganeLista = widoczne.filter((p) => wymagane.includes(p.klucz))
  const dodatkowe = widoczne.filter((p) => !wymagane.includes(p.klucz))
  const sekwencja = [
    ...wymaganeLista.map((p) => p.klucz),
    ...(dodatkowe.length && wymaganeLista.length ? [ROZDROZE] : []),
    ...dodatkowe.map((p) => p.klucz),
    PODSUMOWANIE,
  ]
  const doWyslania = Object.fromEntries(Object.entries(odpowiedzi)
    .filter(([k]) => { const p = pytania.find((q) => q.klucz === k); return !p || widoczne.includes(p) }))

  // Start: pierwsze wymagane bez odpowiedzi; wznowione odpowiedzi z kompletem wymaganych od razu w podsumowaniu.
  const [ekran, setEkran] = useState(() => {
    const brak = wymaganeLista.find((p) => !wypelnione(p, odpowiedzi[p.klucz]))
    return brak ? brak.klucz : sekwencja[0] === PODSUMOWANIE || !wymaganeLista.length ? sekwencja[0] : PODSUMOWANIE
  })
  const [zPodsumowania, setZPodsumowania] = useState(false)
  // Pytania widoczne w chwili „Zmień”: po zmianie pytamy tylko o te, które odsłoniła (nie o wszystkie bez odpowiedzi).
  const przedZmiana = useRef(new Set())
  const [ladowanie, setLadowanie] = useState(false)
  const [potwierdzOdNowa, setPotwierdzOdNowa] = useState(false)
  const [blad, setBlad] = useState(null)
  const pytanie = widoczne.find((p) => p.klucz === ekran) || null
  // Robocza wartość przypięta do klucza pytania: po zmianie ekranu od pierwszego renderu należy do nowego pytania
  // (wcześniej przez jeden render zostawała z poprzedniego i pole liczby czytało wartość z kafli).
  const [rStan, setRStan] = useState({ klucz: null, v: null })
  // `domyslna(o)`: odpowiedź wstępnie zaznaczona, gdy pytanie nie ma jeszcze odpowiedzi (np. „Tak” przy magazynie,
  // gdy cel go wymaga); zatwierdza ją dopiero „Dalej”.
  const startowa = (p) => robocza(p, odpowiedzi[p.klucz] !== undefined ? odpowiedzi[p.klucz] : p.domyslna?.(odpowiedzi))
  const r = pytanie ? (rStan.klucz === pytanie.klucz ? rStan.v : startowa(pytanie)) : null
  const setR = (nowa) => setRStan((s) => {
    const biezaca = s.klucz === pytanie.klucz ? s.v : startowa(pytanie)
    return { klucz: pytanie.klucz, v: typeof nowa === 'function' ? nowa(biezaca) : nowa }
  })
  const naglowekRef = useRef(null)
  const timer = useRef(null)
  const pierwszy = useRef(true)

  useEffect(() => () => clearTimeout(timer.current), [])
  // Pokazany ekran dla pomiaru (`rozdroze`, `podsumowanie` albo klucz pytania), także pierwszy.
  useEffect(() => {
    onEkran?.(ekran === ROZDROZE ? 'rozdroze' : ekran === PODSUMOWANIE ? 'podsumowanie' : ekran)
  }, [ekran]) // eslint-disable-line react-hooks/exhaustive-deps
  // Zmiana ekranu: fokus na nagłówku (czytnik ekranu słyszy nowe pytanie).
  useEffect(() => {
    if (pierwszy.current) { pierwszy.current = false; return }
    const el = naglowekRef.current
    el?.focus({ preventScroll: true })
    // Nagłówek pod pasek strony (ok. 90 px), gdy po długim ekranie (np. cele) został poza widokiem.
    const gora = el?.getBoundingClientRect?.().top
    if (gora != null && (gora < 90 || gora > window.innerHeight * 0.6)) window.scrollBy?.({ top: gora - 90, behavior: 'smooth' })
  }, [ekran]) // eslint-disable-line react-hooks/exhaustive-deps

  // Robocza wartość zeruje się razem z przejściem (nie w efekcie po nim, który mógłby skasować pierwszy wpis).
  const idz = (dokad) => { clearTimeout(timer.current); setBlad(null); setRStan({ klucz: null, v: null }); setEkran(dokad) }
  const nastepny = (odp) => {
    const widoczneTeraz = pytania.filter((p) => !p.warunek || p.warunek(odp))
    const wym = widoczneTeraz.filter((p) => wymagane.includes(p.klucz))
    const dod = widoczneTeraz.filter((p) => !wymagane.includes(p.klucz))
    const sek = [...wym.map((p) => p.klucz), ...(dod.length && wym.length ? [ROZDROZE] : []), ...dod.map((p) => p.klucz), PODSUMOWANIE]
    const i = sek.indexOf(ekran)
    if (zPodsumowania) {
      // Zmiana z podsumowania: najpierw pytania, które ta odpowiedź właśnie odsłoniła (np. cel „Zasilanie pompy
      // ciepła” odsłania „Pompa ciepła już działa?”), potem z powrotem podsumowanie.
      const nowe = sek.slice(i + 1)
        .find((k) => k !== ROZDROZE && k !== PODSUMOWANIE && !przedZmiana.current.has(k) && odp[k] === undefined)
      if (nowe) return nowe
      setZPodsumowania(false)
      return PODSUMOWANIE
    }
    return sek[i + 1] || PODSUMOWANIE
  }
  const zapiszIdz = (wartosc) => {
    const nowe = { ...odpowiedzi, [pytanie.klucz]: wartosc }
    setOdpowiedzi(nowe)
    idz(nastepny(nowe))
  }
  const dalej = (wartosc = r) => {
    if (!pytanie) return
    if (!gotowa(pytanie, wartosc)) return
    zapiszIdz(odpowiedzZRoboczej(pytanie, wartosc))
  }
  const pomin = () => zapiszIdz(null)
  const odNowa = () => {
    setPotwierdzOdNowa(false)
    setZPodsumowania(false)
    setOdpowiedzi(domyslne)
    onOdNowa?.()
    const pierwsze = pytania.find((p) => (!p.warunek || p.warunek(domyslne)) && (!wymagane.length || wymagane.includes(p.klucz)))
    idz(pierwsze ? pierwsze.klucz : PODSUMOWANIE)
  }
  const wstecz = () => {
    const i = sekwencja.indexOf(ekran)
    if (i > 0) idz(sekwencja[i - 1])
  }

  const wybierz = (v) => {
    if (pytanie.typ === 'wielokrotny') {
      setR((x) => (x.includes(v) ? x.filter((y) => y !== v) : [...x, v]))
      return
    }
    setR(v)
    // Pojedynczy wybór przechodzi dalej sam, z krótką chwilą na zobaczenie zaznaczenia.
    clearTimeout(timer.current)
    timer.current = setTimeout(() => dalej(v), 220)
  }

  const naKlawisz = (e) => {
    if (!pytanie || e.altKey || e.ctrlKey || e.metaKey) return
    const wPolu = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)
    if (e.key === 'Enter' && pytanie.typ !== 'komponent' && (wPolu || e.target === e.currentTarget || e.target === naglowekRef.current)) {
      e.preventDefault()
      dalej()
      return
    }
    if (!wPolu && /^[1-9]$/.test(e.key) && pytanie.opcje && (!pytanie.typ || pytanie.typ === 'wybor' || pytanie.typ === 'wielokrotny')) {
      const o = pytanie.opcje[Number(e.key) - 1]
      if (o) { e.preventDefault(); wybierz(o.v) }
    }
  }

  const wyslij = async () => {
    setLadowanie(true); setBlad(null)
    const res = await onDobierz(doWyslania)
    setLadowanie(false)
    if (!res?.ok) setBlad(res?.error || t('prosty.blad'))
  }

  // Postęp: osobno wymagane i dodatkowe, pasek po całej drodze.
  const indeks = sekwencja.indexOf(ekran)
  const procent = Math.round((Math.max(0, indeks) / Math.max(1, sekwencja.length - 1)) * 100)
  const etykietaKroku = (() => {
    if (ekran === PODSUMOWANIE) return t('krokowy.podsumowanie')
    if (ekran === ROZDROZE) return t('krokowy.rozdroze_krok')
    const iw = wymaganeLista.findIndex((p) => p.klucz === ekran)
    if (iw >= 0) return t('krokowy.krok', { nr: iw + 1, z: wymaganeLista.length })
    return t('krokowy.dodatkowe', { nr: dodatkowe.findIndex((p) => p.klucz === ekran) + 1, z: dodatkowe.length })
  })()
  const opcjonalne = pytanie && !wymagane.includes(pytanie.klucz)
  const napisWyniku = typeof tekstPrzycisku === 'function' ? tekstPrzycisku(doWyslania) : tekstPrzycisku

  const tresc = (() => {
    if (ekran === ROZDROZE) {
      return (
        <>
          <h2 ref={naglowekRef} tabIndex={-1} className="kk-tytul">{t('krokowy.rozdroze_tytul')}</h2>
          <p className="kk-opis">{t('krokowy.rozdroze_opis')}</p>
          <div className="kk-kafle kk-kafle-lista" role="group" aria-label={t('krokowy.rozdroze_tytul')}>
            <button type="button" className="kk-kafel" onClick={() => idz(dodatkowe[0].klucz)}>
              <span className="kk-kafel-ikona" aria-hidden="true"><FontAwesomeIcon icon={faSliders} /></span>
              <span className="kk-kafel-tekst">
                <span className="kk-kafel-tytul">{t('krokowy.doprecyzuj')}</span>
                <span className="kk-kafel-opis">{t('krokowy.doprecyzuj_opis', { n: dodatkowe.length })}</span>
              </span>
              <span className="kk-kafel-znak" aria-hidden="true"><FontAwesomeIcon icon={faArrowRight} /></span>
            </button>
            <button type="button" className="kk-kafel" onClick={() => idz(PODSUMOWANIE)}>
              <span className="kk-kafel-ikona" aria-hidden="true"><FontAwesomeIcon icon={faListCheck} /></span>
              <span className="kk-kafel-tekst">
                <span className="kk-kafel-tytul">{t('krokowy.do_podsumowania')}</span>
                <span className="kk-kafel-opis">{t('krokowy.do_podsumowania_opis')}</span>
              </span>
              <span className="kk-kafel-znak" aria-hidden="true"><FontAwesomeIcon icon={faArrowRight} /></span>
            </button>
          </div>
        </>
      )
    }
    if (ekran === PODSUMOWANIE) {
      return (
        <>
          <h2 ref={naglowekRef} tabIndex={-1} className="kk-tytul">{t('krokowy.podsumowanie_tytul')}</h2>
          <dl className="kk-lista">
            {pytania.map((p) => {
              const widoczne_ = widoczne.includes(p)
              const przesadzone = !widoczne_ && p.wPodsumowaniu ? p.wPodsumowaniu(odpowiedzi) : null
              if (!widoczne_ && !przesadzone) return null
              const etykieta = p.etykietaOd?.(odpowiedzi) || p.etykieta
              const wartosc = przesadzone ? przesadzone.wartosc
                : wypelnione(p, odpowiedzi[p.klucz]) ? opisOdpowiedzi(p, odpowiedzi[p.klucz]) : t('szkielet.pominiete')
              const cel = przesadzone ? przesadzone.zmien : p.klucz
              return (
                <div key={p.klucz} className="kk-wiersz">
                  <span className="kk-wiersz-ikona" aria-hidden="true">{p.ikona && <FontAwesomeIcon icon={p.ikona} />}</span>
                  <dt>{etykieta}</dt>
                  <dd>{wartosc}</dd>
                  <button type="button" className="kk-zmien"
                          onClick={() => { przedZmiana.current = new Set(widoczne.map((q) => q.klucz)); setZPodsumowania(true); idz(cel) }}
                          aria-label={t('krokowy.zmien_co', { co: etykieta })}>
                    {t('krokowy.zmien')}
                  </button>
                </div>
              )
            })}
          </dl>
          {blad && <p className="blad" role="alert">{blad}</p>}
          {onOdNowa && (potwierdzOdNowa ? (
            <div className="kk-od-nowa" role="group" aria-label={t('krokowy.od_nowa')}>
              <span>{t('krokowy.od_nowa_pytanie')}</span>
              <button type="button" className="kk-link kk-link-mocny" onClick={odNowa}>{t('krokowy.od_nowa_tak')}</button>
              <button type="button" className="kk-link" onClick={() => setPotwierdzOdNowa(false)}>{t('krokowy.od_nowa_nie')}</button>
            </div>
          ) : (
            <button type="button" className="kk-link kk-od-nowa-link" onClick={() => setPotwierdzOdNowa(true)}>{t('krokowy.od_nowa')}</button>
          ))}
        </>
      )
    }
    if (!pytanie) return null
    const p = pytanie
    return (
      <>
        <h2 ref={naglowekRef} tabIndex={-1} className="kk-tytul">{p.tytul}</h2>
        {p.opis && <p className="kk-opis">{p.opis}</p>}
        {p.dopisek?.(odpowiedzi) && <p className="kk-dopisek">{p.dopisek(odpowiedzi)}</p>}
        {p.opcje && (!p.typ || p.typ === 'wybor' || p.typ === 'wielokrotny') && (
          <div className={`kk-kafle kk-kafle-${kolumny(p.opcje.length)}`} role={p.typ === 'wielokrotny' ? 'group' : 'radiogroup'} aria-label={p.tytul}>
            {p.opcje.map((o, i) => (
              <Kafel key={o.v} o={o} numer={i + 1} wielokrotny={p.typ === 'wielokrotny'}
                     zaznaczony={p.typ === 'wielokrotny' ? r?.includes?.(o.v) : r === o.v} onClick={() => wybierz(o.v)} />
            ))}
          </div>
        )}
        {p.typ === 'lista' && (
          <select className="wejscie kk-pole" aria-label={p.tytul} value={r ?? ''} onChange={(e) => setR(e.target.value)}>
            <option value="">{t('szkielet.wybierz')}</option>
            {p.opcje.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        )}
        {p.typ === 'liczba' && r && (
          <div className="kk-liczba">
            {p.jednostki && (
              <div className="kk-przelacznik" role="radiogroup" aria-label={p.tytul}>
                {p.jednostki.map((j) => (
                  <button key={j.v} type="button" role="radio" aria-checked={r.jednostka === j.v}
                          onClick={() => setR({ ...r, jednostka: j.v })}>{j.l}</button>
                ))}
              </div>
            )}
            {(() => {
              const j = p.jednostki ? p.jednostki.find((x) => x.v === r.jednostka) || p.jednostki[0] : null
              return (
                <label className="kk-pole-liczby">
                  <input className="wejscie kk-pole" inputMode="decimal" autoComplete="off" aria-label={p.tytul}
                         placeholder={j?.placeholder ?? p.liczba.placeholder ?? ''} value={r.tekst}
                         onChange={(e) => setR({ ...r, tekst: e.target.value.replace(/[^\d,.\s]/g, '') })} />
                  <span className="kk-jednostka" aria-hidden="true">{j?.jednostka ?? p.liczba.jednostka}</span>
                </label>
              )
            })()}
          </div>
        )}
        {p.typ === 'komponent' && p.render({ wartosc: r, onZmiana: setR })}
      </>
    )
  })()

  const naPytaniu = Boolean(pytanie) && ekran !== ROZDROZE && ekran !== PODSUMOWANIE
  const dalejAktywne = naPytaniu && gotowa(pytanie, r)

  return (
    <div className="vv-dom kk" onKeyDown={naKlawisz}>
      <div className="kk-postep">
        <span className="kk-postep-tekst" aria-live="polite">{etykietaKroku}</span>
        <span className="kk-pasek" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={procent}
              aria-label={etykietaKroku}>
          <span style={{ width: `${procent}%` }} />
        </span>
      </div>

      <div className="kk-ekran" key={ekran}>{tresc}</div>

      <div className="kk-stopka">
        <button type="button" className="btn btn-obrys kk-przycisk" onClick={wstecz} disabled={indeks <= 0}
                style={indeks <= 0 ? { visibility: 'hidden' } : undefined}>
          {t('krokowy.wstecz')}
        </button>
        <span className="kk-odstep" />
        {naPytaniu && opcjonalne && (
          <button type="button" className="kk-pomin" onClick={pomin}>{t('krokowy.pomin')}</button>
        )}
        {ekran === PODSUMOWANIE ? (
          <button type="button" className="btn btn-glowny kk-przycisk" onClick={wyslij} disabled={ladowanie}>
            {ladowanie ? t('szkielet.ladowanie') : napisWyniku}
          </button>
        ) : ekran === ROZDROZE ? null : (
          <button type="button" className="btn btn-glowny kk-przycisk" onClick={() => dalej()} disabled={!dalejAktywne}>
            {zPodsumowania ? t('krokowy.do_podsumowania_krotko') : t('krokowy.dalej')}
          </button>
        )}
      </div>
    </div>
  )
}

export default KreatorKrokowy
